import { LDSingleKindContext } from 'launchdarkly-js-client-sdk'

import { DEFAULT_LANGUAGE } from '@lyrahealth-inc/shared-app-logic/src/features/intl/constants'
import {
  getGenAiOptOutWithGoogleCatalystOverride,
  isActiveGoogleCatalystEpisode,
} from '@lyrahealth-inc/shared-app-logic/src/features/messaging/utils'
import { ConversationObject } from '@lyrahealth-inc/shared-app-logic/src/models/conversation/Conversation'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'

type LaunchDarklyClientContext = LDSingleKindContext & {
  kind: 'client'
  genAiOptOut: boolean
  country: string
  displayLanguage: string
}

export const filterEpisodesForProvider = (
  episodes: readonly Episode[] | undefined,
  providerId: string,
): readonly Episode[] | undefined => episodes?.filter((episode) => episode.provider_id === providerId)

export const shouldFetchAiCoachConversationEvidence = ({
  episodes,
  genAiOptOut,
  isGoogleCatalystLyraAiEnabled,
}: {
  episodes?: readonly Episode[]
  genAiOptOut?: string
  isGoogleCatalystLyraAiEnabled: boolean
}): boolean =>
  genAiOptOut === 'true' && isGoogleCatalystLyraAiEnabled && !!episodes?.some(isActiveGoogleCatalystEpisode)

export const getEffectiveClientGenAiOptOut = ({
  genAiOptOut,
  conversations,
  episodes,
  isGoogleCatalystLyraAiEnabled,
}: {
  genAiOptOut?: string
  conversations?: readonly ConversationObject[]
  episodes?: readonly Episode[]
  isGoogleCatalystLyraAiEnabled: boolean
}): boolean => {
  const customerGenAiOptOut = genAiOptOut === 'true'

  if (!customerGenAiOptOut) {
    return false
  }

  if (conversations && episodes) {
    return getGenAiOptOutWithGoogleCatalystOverride({
      conversations,
      episodes,
      genAiOptOut: customerGenAiOptOut,
      isGoogleCatalystLyraAiEnabled,
    })
  }

  return true
}

export const buildLaunchDarklyClientContext = ({
  clientId,
  country,
  displayLanguage,
  effectiveGenAiOptOut,
}: {
  clientId?: string
  country?: string
  displayLanguage?: string
  effectiveGenAiOptOut: boolean
}): LaunchDarklyClientContext | undefined => {
  if (!clientId || !country) {
    return undefined
  }

  return {
    kind: 'client',
    key: clientId,
    genAiOptOut: effectiveGenAiOptOut,
    country,
    displayLanguage: displayLanguage ?? DEFAULT_LANGUAGE,
  }
}
